import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';
import { useViewMode } from '@/lib/view-mode-context';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Users, Trophy, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn, formatCurrency } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUserSettings } from '@/lib/user-settings-context';
import { format } from 'date-fns';

interface Player {
  id: string;
  name: string;
}

interface SessionPlayer {
  session_id: string;
  player_id: string;
  net_amount: number | null;
  buy_ins: number;
  sessions: {
    date: string;
  };
}

export default function HeadToHead() {
  const { user } = useAuth();
  const { targetUserId, isPublic } = useViewMode();
  const { settings, loading: settingsLoading } = useUserSettings();
  const navigate = useNavigate();
  const [players, setPlayers] = useState<Player[]>([]);
  const [player1Id, setPlayer1Id] = useState<string>('');
  const [player2Id, setPlayer2Id] = useState<string>('');
  const [allSessionPlayers, setAllSessionPlayers] = useState<SessionPlayer[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!settingsLoading && !settings.head_to_head) {
      navigate('/');
      return;
    }

    if (!targetUserId) return;

    const fetchData = async () => {
      const { data: playersData } = await supabase
        .from('players')
        .select('id, name')
        .eq('created_by', targetUserId)
        .order('name');
      
      setPlayers(playersData || []);

      const { data: spData } = await supabase
        .from('session_players')
        .select('session_id, player_id, net_amount, buy_ins, sessions!inner(date, created_by)')
        .eq('sessions.created_by', targetUserId);

      const formattedData = (spData || []).map((sp: any) => ({
        ...sp,
        date: sp.sessions?.date || ''
      })).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      setAllSessionPlayers(formattedData as any);
      setLoading(false);
    };

    fetchData();
  }, [targetUserId, navigate, settingsLoading, settings.head_to_head]);

  const comparison = useMemo(() => {
    if (!player1Id || !player2Id || player1Id === player2Id) return null;

    const p1Sessions = allSessionPlayers.filter(sp => sp.player_id === player1Id);
    const p2Sessions = allSessionPlayers.filter(sp => sp.player_id === player2Id);

    const commonSessionIds = p1Sessions
      .filter(p1 => p2Sessions.some(p2 => p2.session_id === p1.session_id))
      .map(sp => sp.session_id);

    const p1Common = p1Sessions.filter(sp => commonSessionIds.includes(sp.session_id));
    const p2Common = p2Sessions.filter(sp => commonSessionIds.includes(sp.session_id));

    let p1Wins = 0;
    let p2Wins = 0;
    let draws = 0;

    commonSessionIds.forEach(sid => {
      const p1 = p1Common.find(sp => sp.session_id === sid);
      const p2 = p2Common.find(sp => sp.session_id === sid);
      if (p1 && p2) {
        if ((p1.net_amount || 0) > (p2.net_amount || 0)) p1Wins++;
        else if ((p2.net_amount || 0) > (p1.net_amount || 0)) p2Wins++;
        else draws++;
      }
    });

    const p1TotalNet = p1Common.reduce((sum, sp) => sum + (sp.net_amount || 0), 0);
    const p2TotalNet = p2Common.reduce((sum, sp) => sum + (sp.net_amount || 0), 0);

    return {
      commonSessionsCount: commonSessionIds.length,
      p1Wins,
      p2Wins,
      draws,
      p1TotalNet,
      p2TotalNet,
      p1Name: players.find(p => p.id === player1Id)?.name || 'Player 1',
      p2Name: players.find(p => p.id === player2Id)?.name || 'Player 2',
    };
  }, [player1Id, player2Id, allSessionPlayers, players]);

  if (settingsLoading) return null;
  if (!settings.head_to_head) return null;

  return (
    <div className="py-6 space-y-6 pb-24">
      <div className="flex items-center gap-2">
        <Button size="icon" variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
            <Users className="h-5 w-5 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight">Head-to-Head</h1>
            <p className="text-sm text-muted-foreground">Compare two players</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Player 1</label>
          <Select value={player1Id} onValueChange={setPlayer1Id}>
            <SelectTrigger>
              <SelectValue placeholder="Select player" />
            </SelectTrigger>
            <SelectContent>
              {players.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Player 2</label>
          <Select value={player2Id} onValueChange={setPlayer2Id}>
            <SelectTrigger>
              <SelectValue placeholder="Select player" />
            </SelectTrigger>
            <SelectContent>
              {players.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : comparison ? (
        <div className="space-y-6">
          <div className="grid grid-cols-3 gap-4 items-center">
            <div className="text-center space-y-1">
              <p className="text-2xl font-black">{comparison.p1Wins}</p>
              <p className="text-[10px] text-muted-foreground uppercase font-bold">Wins</p>
            </div>
            <div className="text-center bg-muted/50 rounded-lg py-2">
              <p className="text-sm font-bold text-muted-foreground uppercase">Draws</p>
              <p className="text-xl font-black">{comparison.draws}</p>
            </div>
            <div className="text-center space-y-1">
              <p className="text-2xl font-black">{comparison.p2Wins}</p>
              <p className="text-[10px] text-muted-foreground uppercase font-bold">Wins</p>
            </div>
          </div>

          <div className="relative h-4 bg-muted rounded-full overflow-hidden flex">
            <div 
              className="bg-primary h-full transition-all duration-500" 
              style={{ width: `${comparison.commonSessionsCount > 0 ? (comparison.p1Wins / comparison.commonSessionsCount) * 100 : 50}%` }}
            />
            <div 
              className="bg-slate-400 h-full transition-all duration-500" 
              style={{ width: `${comparison.commonSessionsCount > 0 ? (comparison.draws / comparison.commonSessionsCount) * 100 : 0}%` }}
            />
            <div 
              className="bg-secondary h-full transition-all duration-500 flex-1" 
            />
          </div>

          <Card className="border-0 shadow-sm overflow-hidden">
            <CardHeader className="bg-muted/30 p-4">
              <CardTitle className="text-sm text-center font-bold uppercase tracking-wider">Score in Common Sessions</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="flex justify-between items-center gap-8">
                <div className="flex-1 text-center space-y-1">
                  <p className="text-xs text-muted-foreground font-medium mb-1 truncate">{comparison.p1Name}</p>
                  <p className={cn(
                    "text-xl font-mono font-black",
                    comparison.p1TotalNet > 0 ? "text-success" : comparison.p1TotalNet < 0 ? "text-destructive" : ""
                  )}>
                    {comparison.p1TotalNet > 0 ? '+' : ''}{formatCurrency(comparison.p1TotalNet)}
                  </p>
                </div>
                <div className="flex flex-col items-center">
                   <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold">VS</div>
                </div>
                <div className="flex-1 text-center space-y-1">
                  <p className="text-xs text-muted-foreground font-medium mb-1 truncate">{comparison.p2Name}</p>
                  <p className={cn(
                    "text-xl font-mono font-black",
                    comparison.p2TotalNet > 0 ? "text-success" : comparison.p2TotalNet < 0 ? "text-destructive" : ""
                  )}>
                    {comparison.p2TotalNet > 0 ? '+' : ''}{formatCurrency(comparison.p2TotalNet)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-3">
             <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Common Sessions ({comparison.commonSessionsCount})</h2>
             {comparison.commonSessionsCount === 0 ? (
               <p className="text-center py-8 text-muted-foreground text-sm">No common sessions found between these players.</p>
             ) : (
               <div className="space-y-2">
                 {allSessionPlayers
                   .filter(sp => sp.player_id === player1Id && comparison.commonSessionsCount > 0)
                   .filter(sp => allSessionPlayers.some(sp2 => sp2.player_id === player2Id && sp2.session_id === sp.session_id))
                   .slice(0, 10)
                   .map(p1Sp => {
                     const p2Sp = allSessionPlayers.find(sp => sp.player_id === player2Id && sp.session_id === p1Sp.session_id);
                     return (
                       <div key={p1Sp.session_id} className="bg-white dark:bg-slate-900 p-3 rounded-lg shadow-sm flex justify-between items-center text-sm border border-border/50">
                         <div className="flex flex-col">
                           <span className="font-semibold">{format(new Date(p1Sp.sessions.date), 'MMM d, yyyy')}</span>
                         </div>
                         <div className="flex items-center gap-4 font-mono">
                           <span className={cn(p1Sp.net_amount! > 0 ? "text-success" : "text-destructive")}>
                             {p1Sp.net_amount! >= 0 ? '+' : ''}{p1Sp.net_amount}
                           </span>
                           <span className="text-muted-foreground text-[10px]">vs</span>
                           <span className={cn(p2Sp!.net_amount! > 0 ? "text-success" : "text-destructive")}>
                             {p2Sp!.net_amount! >= 0 ? '+' : ''}{p2Sp!.net_amount}
                           </span>
                         </div>
                       </div>
                     );
                   })
                 }
               </div>
             )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 text-center px-6">
          <Users className="h-12 w-12 text-muted-foreground/30 mb-4" />
          <h3 className="text-lg font-bold mb-1">Select two players</h3>
          <p className="text-sm text-muted-foreground max-w-[250px]">Select two different players from the dropdowns above to compare their performance.</p>
        </div>
      )}
    </div>
  );
}
